import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEY } from './constants';
import { TQueryData } from './types';
import { filterOutEstimationById } from './utils/filterOutEstimationById';
import {
  deleteEstimationHistoryItem,
  TDeleteEstimationHistoryItemMapped,
} from '../../services/estimationHistory/deleteEstimationHistoryItem';
import { useApplicationContext } from '../../utils/applicationContext';

/** Запрос на удаление данных прошлой оценки по ее id */
export const useDeleteEstimationHistoryItemQuery = () => {
  const queryClient = useQueryClient();
  const context = useApplicationContext();

  const {
    mutate: deleteEstimation,
    isLoading,
    variables: deletingId,
  } = useMutation<TDeleteEstimationHistoryItemMapped, Error, number, TQueryData>(
    QUERY_KEY,
    id => deleteEstimationHistoryItem(context, { id }),
    {
      onMutate: async idToDelete => {
        /** Реализация оптимистичного удаления. Карточка из UI удаляется мгновенно, не дожидаясь ответа от API */

        /** Если какие-то запросы были в прогрессе, отменить их, чтоб ничего не сломалось */
        await queryClient.cancelQueries(['todos']);

        /** Бэкап данных ДО удаления на случай ошибки API */
        const snapshotData = queryClient.getQueryData(QUERY_KEY) as NonNullable<TQueryData>;

        queryClient.setQueryData<TQueryData>(QUERY_KEY, previousData =>
          filterOutEstimationById(previousData, idToDelete),
        );

        return snapshotData;
      },
      onError: (err, newTodo, snapshotData) => {
        /** Если что-то пошло не так, происходит откат к данным ДО удаления */
        queryClient.setQueryData(QUERY_KEY, snapshotData);
      },
      onSettled: () => {
        /** Инвалидация данных, синхронизация с api */
        queryClient.invalidateQueries(QUERY_KEY);
      },
    },
  );

  const deleteInProgressId = isLoading ? deletingId : undefined;

  return { deleteEstimation, deleteInProgressId };
};
